import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  StandardizedTable,
  TableColumn,
  TableRow,
  formatCurrency,
  formatNumber,
  TrendIndicator,
} from "@/components/ui/standardized-table";
import { CarregandoConteudo } from "@/components/ui/carregando-conteudo";
import GlobalFilter from "@/components/common/GlobalFilter";
import CampaignFiltersControl from "@/components/common/CampaignFiltersControl";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { useAccount } from "@/contexts/account.context";
import { useToast } from "@/hooks/use-toast";
import {
  proxyRegionsSummaryApiV1RegionsDashboardSummaryGet,
  proxyRegionsStatesApiV1RegionsDashboardStatesGet,
  proxyRegionsItemsApiV1RegionsDashboardItemsGet,
  proxyRegionsCitiesApiV1RegionsDashboardCitiesGet,
  proxyRegionsCategoryMatrixApiV1RegionsDashboardCategoryMatrixGet,
} from "@/backend-sdk/client/sdk.gen";
import type {
  ProxyRegionsSummaryApiV1RegionsDashboardSummaryGetData,
  RegionsDashboardSummaryResponse,
  RegionsDashboardStatesResponse,
  RegionsDashboardItemsResponse,
  RegionsDashboardCitiesResponse,
  RegionsCategoryMatrixResponse,
  RegionSummaryItem,
} from "@/backend-sdk/client/types.gen";
import { TrendingDown, TrendingUp } from "lucide-react";
import { formatSignedPercent } from "@/utils/numberFormat";
import type { TableQueryDTO } from "@/types/table-query";
import {
  DrilldownProvider,
  type DrillDimension,
} from "@/features/drilldown/drilldown.context";
import { useDrilldownOptional } from "@/features/drilldown";
import { ActiveFiltersDisplay } from "@/features/drilldown/ActiveFiltersDisplay";
import useCampaignFiltersState from "@/hooks/useCampaignFiltersState";
import {
  CarregandoSobreposto,
  CarregandoValor,
  EstadoDoValor,
} from "@/components/ui/estado-do-valor";
import {
  MIDIA_PAGA,
  type RequisitoDeFonte,
} from "@/lib/motivo-do-indisponivel";

/** O que cada linha dos cards de região precisa para existir. */
const FONTES_DA_REGIAO: Readonly<
  Record<"ga4" | "ga4ComMidia", readonly RequisitoDeFonte[]>
> = {
  ga4: ["ga4"],
  // ROAS e CPS dividem pela verba de Google Ads e Meta Ads rateada pelas sessões da região.
  ga4ComMidia: ["ga4", MIDIA_PAGA],
};

const REGION_CARD_STYLES: Record<string, { badge: string; badgeText: string }> =
  {
    sudeste: { badge: "bg-red-600", badgeText: "text-white" },
    sul: { badge: "bg-blue-600", badgeText: "text-white" },
    "centro-oeste": { badge: "bg-yellow-500", badgeText: "text-gray-800" },
    nordeste: { badge: "bg-orange-500", badgeText: "text-white" },
    norte: { badge: "bg-green-600", badgeText: "text-white" },
  };

const DEFAULT_REGIONS_ORDER = [
  "sudeste",
  "sul",
  "centro-oeste",
  "nordeste",
  "norte",
];

type Trend = "up" | "down" | "neutral";

const getComparisonParam = (comparisonType?: string) => {
  if (comparisonType === "previousYear") return "previous_year";
  return "previous_period";
};

const getTrend = (value?: number | null): Trend => {
  if (value === null || value === undefined) return "neutral";
  if (value > 0) return "up";
  if (value < 0) return "down";
  return "neutral";
};

const formatPercentage = (value?: number | null, decimals: number = 2) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "-";
  }
  return `${Number(value).toFixed(decimals)}%`;
};

const formatValue = (
  value?: number | null,
  type: "currency" | "percent" | "number" = "number",
) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "N/D";
  }
  if (type === "currency") return formatCurrency(value);
  if (type === "percent") return `${value.toFixed(2)}%`;
  return Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const LocalTrendIndicator = ({
  value,
  trend,
}: {
  value: number;
  trend: Trend;
}) => (
  <div className="flex items-center justify-end">
    <span
      className={`text-xs font-semibold mr-2 ${trend === "up" ? "text-success-soft" : trend === "down" ? "text-error-soft" : "text-muted-foreground"}`}
    >
      {formatSignedPercent(value, 1)}
    </span>
    {trend === "neutral" ? null : trend === "up" ? (
      <TrendingUp className="h-3.5 w-3.5 text-success-soft" />
    ) : (
      <TrendingDown className="h-3.5 w-3.5 text-error-soft" />
    )}
  </div>
);

/**
 * Os cross-filters que as cinco rotas desta página declaram — M-211.
 *
 * O tipo sai do SDK gerado, e não de uma lista escrita à mão: um nome que a rota deixe de
 * declarar deixa de compilar aqui. `summary` é a rota de referência porque é a única que
 * recebe os dois (as outras quatro excluem a própria dimensão, do lado da tela).
 */
type RegiaoCrossFilterKey = Extract<
  keyof ProxyRegionsSummaryApiV1RegionsDashboardSummaryGetData["query"],
  `filter_${string}`
>;
type RegiaoCrossFilters = Pick<
  ProxyRegionsSummaryApiV1RegionsDashboardSummaryGetData["query"],
  RegiaoCrossFilterKey
>;

const RegiaoPage: React.FC = () => {
  // M-210: o drill de produto saiu. `itemName` é dimensão de escopo de item, e a Data API
  // recusa métrica de escopo de sessão junto dela — nenhuma das cinco rotas desta página
  // tinha como aplicar `filter_product`, e o clique só desenhava um chip que não recortava
  // nada. Categoria saiu junto: tabela nenhuma da tela publica categoria como rótulo
  // clicável (a matriz publica as categorias em colunas, e o clique dela é no estado).
  const hierarchies = useMemo(
    (): DrillDimension[][] => [["region", "city"]],
    [],
  );

  return (
    <DrilldownProvider hierarchies={hierarchies}>
      <RegiaoPageContent />
    </DrilldownProvider>
  );
};

const RegiaoPageContent: React.FC = () => {
  const { filter: filterState } = useGlobalFilter();
  const { selectedAccount } = useAccount();
  const { toast } = useToast();
  const drilldown = useDrilldownOptional();
  const { campaignFiltersNonce, handleCampaignFiltersSaved } =
    useCampaignFiltersState();

  const [summary, setSummary] =
    useState<RegionsDashboardSummaryResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [erroSummary, setErroSummary] = useState(false);

  const [statesData, setStatesData] =
    useState<RegionsDashboardStatesResponse | null>(null);
  const [statesLoading, setStatesLoading] = useState(false);
  const [statesPage, setStatesPage] = useState(1);
  const STATES_PAGE_SIZE = 10;
  const [statesQuery, setStatesQuery] = useState<TableQueryDTO>({});

  const [itemsData, setItemsData] =
    useState<RegionsDashboardItemsResponse | null>(null);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsPage, setItemsPage] = useState(1);
  const ITEMS_PAGE_SIZE = 10;
  const [itemsQuery, setItemsQuery] = useState<TableQueryDTO>({});

  const [citiesData, setCitiesData] =
    useState<RegionsDashboardCitiesResponse | null>(null);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [citiesPage, setCitiesPage] = useState(1);
  const CITIES_PAGE_SIZE = 10;
  const [citiesQuery, setCitiesQuery] = useState<TableQueryDTO>({});

  const [categoryMatrixData, setCategoryMatrixData] =
    useState<RegionsCategoryMatrixResponse | null>(null);

  const [categoryMatrixLoading, setCategoryMatrixLoading] = useState(false);
  const [categoryMatrixPage, setCategoryMatrixPage] = useState(1);
  const CATEGORY_MATRIX_PAGE_SIZE = 10;
  const [categoryMatrixQuery, setCategoryMatrixQuery] = useState<TableQueryDTO>(
    {},
  );

  const customerUuid =
    selectedAccount?.customer_uuid ||
    localStorage.getItem("active_customer_uuid") ||
    undefined;
  const campaignCustomerUuid = customerUuid || null;
  const accountId = selectedAccount?.account_id ?? undefined;

  const filterParams = useMemo(() => {
    if (!filterState) return null;
    const start = formatDateToISO(filterState.dateRange.startDate);
    const end = formatDateToISO(filterState.dateRange.endDate);
    const comparison = getComparisonParam(filterState.comparison?.type);
    return { start, end, comparison };
  }, [filterState]);

  // ---------- Cross-filter: extraParams derivados do drilldown ----------

  /**
   * Os cross-filters da página, excluindo o da própria dimensão da tabela.
   *
   * M-211: o retorno é tipado com os **dois** nomes que as cinco rotas declaram
   * (`filter_region` e `filter_city`), e não um `Record<string, string>` do objeto inteiro
   * do provider. A tela mandava quatro `filter_*` que rota nenhuma declarava, e a query é
   * lax: eles atravessavam em silêncio e a tabela respondia o período inteiro sob o chip de
   * um filtro ativo. Com o tipo, um nome que a rota não conhece não compila.
   */
  const buildExtraParams = (
    excludeDimension?: DrillDimension,
  ): Partial<RegiaoCrossFilters> => {
    const ativos = drilldown?.apiFilters;
    if (!ativos) return {};
    const params: Partial<RegiaoCrossFilters> = {
      filter_region: ativos.filter_region,
      filter_city: ativos.filter_city,
    };
    if (excludeDimension) {
      delete params[`filter_${excludeDimension}` as keyof RegiaoCrossFilters];
    }
    return params;
  };

  // ---------- Cross-filter: handlers de clique ----------

  const handleCategoryMatrixRowClick = useCallback(
    (row: TableRow, _index: number, event?: React.MouseEvent) => {
      if (!drilldown) return;
      const regionName = row.region as string;
      if (!regionName || regionName === "N/D") return;
      const isMultiSelect = event?.ctrlKey || event?.metaKey;
      drilldown.selectRow("region", regionName, regionName, isMultiSelect);
    },
    [drilldown],
  );

  const handleCityRowClick = useCallback(
    (row: TableRow, _index: number, event?: React.MouseEvent) => {
      if (!drilldown) return;
      const cityName = row.city as string;
      if (!cityName || cityName === "N/D") return;
      const isMultiSelect = event?.ctrlKey || event?.metaKey;
      drilldown.selectRow("city", cityName, cityName, isMultiSelect);
    },
    [drilldown],
  );

  const handleStatesRowClick = useCallback(
    (row: TableRow, _index: number, event?: React.MouseEvent) => {
      if (!drilldown) return;
      const stateName = row.state as string;
      if (!stateName || stateName === "N/D") return;
      const isMultiSelect = event?.ctrlKey || event?.metaKey;
      drilldown.selectRow("region", stateName, stateName, isMultiSelect);
    },
    [drilldown],
  );

  // ---------- Cross-filter: isSelected callbacks ----------

  const isCategoryMatrixRowSelected = useCallback(
    (row: TableRow): boolean => {
      if (!drilldown) return false;
      return drilldown.isSelected("region", row.region as string);
    },
    [drilldown],
  );

  const isCityRowSelected = useCallback(
    (row: TableRow): boolean => {
      if (!drilldown) return false;
      return drilldown.isSelected("city", row.city as string);
    },
    [drilldown],
  );

  const isStatesRowSelected = useCallback(
    (row: TableRow): boolean => {
      if (!drilldown) return false;
      return drilldown.isSelected("region", row.state as string);
    },
    [drilldown],
  );

  // ---------- Reset pages ao mudar filtros cruzados ----------

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStatesPage(1);
    setItemsPage(1);
    setCitiesPage(1);
    setCategoryMatrixPage(1);
  }, [drilldown?.apiFiltersKey]);

  // ---------- Fetch data ----------

  useEffect(() => {
    if (!filterParams || !customerUuid) return;

    const fetchSummary = async () => {
      try {
        setSummaryLoading(true);
        setErroSummary(false);
        // ⚠️ Os cinco cards **são** o publicador do rótulo de região: clicar em "Sudeste"
        // manda `filter_region=Sudeste`, e mandá-lo de volta a esta rota zeraria os outros
        // quatro cards. É a mesma exclusão das outras três tabelas — achado do code review.
        const extra = buildExtraParams("region");
        const query = {
          start_date: filterParams.start,
          end_date: filterParams.end,
          comparison: filterParams.comparison,
          customer_uuid: customerUuid,
          ...extra,
        };
        const { data: summaryResp } =
          await proxyRegionsSummaryApiV1RegionsDashboardSummaryGet({
            query,
            throwOnError: true,
          });
        setSummary(summaryResp);
      } catch (error) {
        console.error("Erro ao carregar resumo/regiões:", error);
        setErroSummary(true);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os dados de regiões.",
          variant: "destructive",
        });
        setSummary(null);
      } finally {
        setSummaryLoading(false);
      }
    };

    fetchSummary();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStatesPage(1);
    setItemsPage(1);
    setCitiesPage(1);
    setCategoryMatrixPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterParams,
    customerUuid,
    toast,
    campaignFiltersNonce,
    drilldown?.apiFiltersKey,
  ]);

  useEffect(() => {
    if (!filterParams || !customerUuid) return;

    const fetchStates = async () => {
      try {
        setStatesLoading(true);
        const extra = buildExtraParams("region");
        const query = {
          start_date: filterParams.start,
          end_date: filterParams.end,
          comparison: filterParams.comparison,
          customer_uuid: customerUuid,
          page: statesPage,
          page_size: STATES_PAGE_SIZE,
          q: statesQuery?.q,
          sort_by: statesQuery?.sortBy,
          sort_dir: statesQuery?.sortDir,
          filters: statesQuery?.filters?.length
            ? JSON.stringify(statesQuery.filters)
            : undefined,
          ...extra,
        };
        const { data: response } =
          await proxyRegionsStatesApiV1RegionsDashboardStatesGet({
            query,
            throwOnError: true,
          });
        setStatesData(response);
      } catch (error) {
        console.error("Erro ao carregar dados por estado:", error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os estados.",
          variant: "destructive",
        });
        setStatesData(null);
      } finally {
        setStatesLoading(false);
      }
    };

    fetchStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterParams,
    customerUuid,
    statesPage,
    statesQuery,
    toast,
    campaignFiltersNonce,
    drilldown?.apiFiltersKey,
  ]);

  useEffect(() => {
    if (!filterParams || !customerUuid) return;

    const fetchItems = async () => {
      try {
        setItemsLoading(true);
        const extra = buildExtraParams();
        const query = {
          start_date: filterParams.start,
          end_date: filterParams.end,
          comparison: filterParams.comparison,
          customer_uuid: customerUuid,
          page: itemsPage,
          page_size: ITEMS_PAGE_SIZE,
          q: itemsQuery?.q,
          sort_by: itemsQuery?.sortBy,
          sort_dir: itemsQuery?.sortDir,
          filters: itemsQuery?.filters?.length
            ? JSON.stringify(itemsQuery.filters)
            : undefined,
          ...extra,
        };
        const { data: response } =
          await proxyRegionsItemsApiV1RegionsDashboardItemsGet({
            query,
            throwOnError: true,
          });
        setItemsData(response);
      } catch (error) {
        console.error("Erro ao carregar itens:", error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os itens.",
          variant: "destructive",
        });
        setItemsData(null);
      } finally {
        setItemsLoading(false);
      }
    };

    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterParams,
    customerUuid,
    itemsPage,
    itemsQuery,
    toast,
    campaignFiltersNonce,
    drilldown?.apiFiltersKey,
  ]);

  useEffect(() => {
    if (!filterParams || !customerUuid) return;

    const fetchCities = async () => {
      try {
        setCitiesLoading(true);
        const extra = buildExtraParams("city");
        const query = {
          start_date: filterParams.start,
          end_date: filterParams.end,
          comparison: filterParams.comparison,
          customer_uuid: customerUuid,
          page: citiesPage,
          page_size: CITIES_PAGE_SIZE,
          q: citiesQuery?.q,
          sort_by: citiesQuery?.sortBy,
          sort_dir: citiesQuery?.sortDir,
          filters: citiesQuery?.filters?.length
            ? JSON.stringify(citiesQuery.filters)
            : undefined,
          ...extra,
        };
        const { data: response } =
          await proxyRegionsCitiesApiV1RegionsDashboardCitiesGet({
            query,
            throwOnError: true,
          });
        setCitiesData(response);
      } catch (error) {
        console.error("Erro ao carregar cidades:", error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os dados de cidades.",
          variant: "destructive",
        });
        setCitiesData(null);
      } finally {
        setCitiesLoading(false);
      }
    };

    fetchCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterParams,
    customerUuid,
    citiesPage,
    citiesQuery,
    toast,
    campaignFiltersNonce,
    drilldown?.apiFiltersKey,
  ]);

  useEffect(() => {
    if (!filterParams || !customerUuid) return;

    const fetchCategoryMatrix = async () => {
      try {
        setCategoryMatrixLoading(true);
        const extra = buildExtraParams("region");
        const query = {
          start_date: filterParams.start,
          end_date: filterParams.end,
          customer_uuid: customerUuid,
          ...extra,
        };
        const { data: response } =
          await proxyRegionsCategoryMatrixApiV1RegionsDashboardCategoryMatrixGet(
            {
              query,
              throwOnError: true,
            },
          );
        setCategoryMatrixData(response);
      } catch (error) {
        console.error("Erro ao carregar matriz de categorias:", error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar a matriz de categorias.",
          variant: "destructive",
        });
        setCategoryMatrixData(null);
      } finally {
        setCategoryMatrixLoading(false);
      }
    };

    fetchCategoryMatrix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterParams,
    customerUuid,
    toast,
    categoryMatrixPage,
    categoryMatrixQuery,
    campaignFiltersNonce,
    drilldown?.apiFiltersKey,
  ]);

  const summaryRegions = summary?.regions || [];

  const orderedRegions = useMemo(() => {
    const map = new Map(
      (summary?.regions || []).map((region) => [region.slug, region]),
    );
    return DEFAULT_REGIONS_ORDER.map((slug) => map.get(slug)).filter(
      (region): region is RegionSummaryItem => Boolean(region),
    );
  }, [summary]);

  const regionGroups = useMemo(() => {
    return {
      left: orderedRegions.filter((region) =>
        ["sudeste", "sul", "centro-oeste"].includes(region.slug),
      ),
      right: orderedRegions.filter((region) =>
        ["nordeste", "norte"].includes(region.slug),
      ),
    };
  }, [orderedRegions]);

  const itemsColumns: TableColumn[] = [
    { key: "item_name", header: "Nome do item", align: "left" },
    {
      key: "items_viewed",
      header: "Itens vistos",
      align: "right",
      render: (value) => formatNumber(Number(value || 0)),
    },
    {
      key: "items_viewed_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "items_added_to_cart",
      header: "Carrinhos",
      align: "right",
      render: (value) => formatNumber(Number(value || 0)),
    },
    {
      key: "items_added_to_cart_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "cart_rate",
      header: "TX Carrinho",
      align: "right",
      render: (value) => formatPercentage(Number(value)),
    },
    {
      key: "cart_rate_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "items_purchased",
      header: "Itens comprados",
      align: "right",
      render: (value) => formatNumber(Number(value || 0)),
    },
    {
      key: "items_purchased_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "conversion_rate",
      header: "TX Conv. itens",
      align: "right",
      render: (value) => formatPercentage(Number(value)),
    },
    {
      key: "conversion_rate_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "item_revenue",
      header: "Receita do item",
      align: "right",
      render: (value) => formatCurrency(Number(value || 0)),
    },
    {
      key: "item_revenue_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "users",
      header: "Usuários",
      align: "right",
      visible: false,
      render: (value) =>
        value !== null && value !== undefined
          ? formatNumber(Number(value))
          : "N/D",
    },
    {
      key: "users_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "sessions",
      header: "Sessões",
      align: "right",
      visible: false,
      render: (value) =>
        value !== null && value !== undefined
          ? formatNumber(Number(value))
          : "N/D",
    },
    {
      key: "sessions_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "tx_conv_sessions",
      header: "TX Conv. Itens",
      align: "right",
      visible: false,
      render: (value) =>
        value !== null && value !== undefined
          ? formatPercentage(Number(value))
          : "N/D",
    },
    {
      key: "tx_conv_sessions_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
  ];

  const statesColumns: TableColumn[] = [
    { key: "state", header: "Estado", align: "left" },
    {
      key: "sessions",
      header: "Sessões",
      align: "right",
      render: (value) => formatNumber(Number(value || 0)),
    },
    {
      key: "sessions_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "cps",
      header: "CPS",
      align: "right",
      render: (value) => formatCurrency(Number(value || 0)),
    },
    {
      key: "cps_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "invest_meta",
      header: "Invest. Meta",
      align: "right",
      render: (value) => formatCurrency(Number(value || 0)),
    },
    {
      key: "invest_meta_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "invest_google",
      header: "Invest. Google",
      align: "right",
      render: (value) => formatCurrency(Number(value || 0)),
    },
    {
      key: "invest_google_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "conversion_rate",
      header: "TX Conversão",
      align: "right",
      render: (value) => formatPercentage(Number(value)),
    },
    {
      key: "conversion_rate_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "roas",
      header: "ROAS",
      align: "right",
      render: (value) =>
        value === null || value === undefined
          ? "-"
          : Number(value || 0).toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
    },
    {
      key: "roas_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "revenue",
      header: "Receita total",
      align: "right",
      render: (value) => formatCurrency(Number(value || 0)),
    },
    {
      key: "revenue_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "ticket_medio",
      header: "TM",
      align: "right",
      visible: false,
      render: (value) =>
        value !== null && value !== undefined
          ? formatCurrency(Number(value))
          : "N/D",
    },
    {
      key: "ticket_medio_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "transactions",
      header: "Pedidos",
      align: "right",
      visible: false,
      render: (value) =>
        value !== null && value !== undefined
          ? formatNumber(Number(value))
          : "N/D",
    },
    {
      key: "transactions_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
  ];

  const citiesColumns: TableColumn[] = [
    { key: "city", header: "Cidade", align: "left" },
    {
      key: "sessions",
      header: "Sessões",
      align: "right",
      render: (value) => formatNumber(Number(value || 0)),
    },
    {
      key: "sessions_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "orders",
      header: "Pedidos",
      align: "right",
      render: (value) => formatNumber(Number(value || 0)),
    },
    {
      key: "orders_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "conversion_rate",
      header: "TX Conversão",
      align: "right",
      render: (value) => formatPercentage(Number(value)),
    },
    {
      key: "conversion_rate_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "revenue",
      header: "Receita total",
      align: "right",
      render: (value) => formatCurrency(Number(value || 0)),
    },
    {
      key: "revenue_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
    {
      key: "ticket_medio",
      header: "TM",
      align: "right",
      visible: false,
      render: (value) =>
        value !== null && value !== undefined
          ? formatCurrency(Number(value))
          : "N/D",
    },
    {
      key: "ticket_medio_change_percentage",
      header: "% Δ",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          "-"
        ) : (
          <TrendIndicator value={Number(value)} />
        ),
    },
  ];

  const categoryMatrixTable = useMemo(() => {
    if (!categoryMatrixData || categoryMatrixData.items.length === 0)
      return null;

    const categories = categoryMatrixData.categories || [];
    const columns: TableColumn[] = [
      {
        key: "region",
        header: "Região",
        type: "text",
        align: "left",
        width: "16ch",
      },
      ...categories.map((cat) => ({
        key: cat,
        header: cat,
        type: "currency" as const,
        align: "right" as const,
        width: "16ch",
        render: (value: unknown) => formatCurrency(Number(value || 0)),
      })),
    ];

    const data = categoryMatrixData.items.map((item) => {
      const row: { region: string; [category: string]: string | number } = {
        region: item.region,
      };
      const props = item.additional_properties ?? {};
      categories.forEach((cat) => {
        row[cat] = props[cat] ?? 0;
      });
      return row;
    });

    return { columns, data };
  }, [categoryMatrixData]);

  const handleRegionCardClick = useCallback(
    (region: RegionSummaryItem, event: React.MouseEvent) => {
      if (!drilldown) return;
      const isMultiSelect = event.ctrlKey || event.metaKey;
      drilldown.selectRow("region", region.label, region.label, isMultiSelect);
    },
    [drilldown],
  );

  const renderRegionCard = (region: RegionSummaryItem) => {
    const style =
      REGION_CARD_STYLES[region.slug] || REGION_CARD_STYLES["sudeste"];
    const isSelected = drilldown?.isSelected("region", region.label) ?? false;

    const metricRow = (
      label: string,
      metric: RegionSummaryItem["metrics"][keyof RegionSummaryItem["metrics"]],
      type: "currency" | "percent" | "number",
      fontes: readonly RequisitoDeFonte[],
    ) => (
      <div className="flex justify-between items-center">
        <span className="text-xs text-muted-foreground">{label}</span>
        <div className="text-right">
          <div className="font-semibold text-sm text-foreground">
            <EstadoDoValor
              carregando={false}
              disponivel={
                typeof metric?.value === "number" &&
                Number.isFinite(metric.value)
              }
              fontes={fontes}
            >
              {formatValue(metric?.value, type)}
            </EstadoDoValor>
          </div>
          {metric?.change_percentage !== null &&
          metric?.change_percentage !== undefined ? (
            <div className="text-xs flex items-center justify-end">
              <LocalTrendIndicator
                value={metric.change_percentage}
                trend={metric.trend as Trend}
              />
            </div>
          ) : null}
        </div>
      </div>
    );

    return (
      <div
        key={region.slug}
        className={`col-span-12 rounded-lg p-3 border cursor-pointer transition-all duration-200 ${
          isSelected
            ? "border-primary ring-2 ring-primary/30 bg-primary/5"
            : "border-border hover:border-primary/50 hover:shadow-sm"
        }`}
        onClick={(e) => handleRegionCardClick(region, e)}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
      >
        <h3 className="font-semibold text-base mb-3 text-foreground">
          {region.label}
        </h3>
        <div className="mb-4 grid grid-cols-3 gap-2">
          {region.states.map((state) => (
            <div
              key={state}
              className={`${style.badge} rounded px-3 py-2 text-sm font-medium ${style.badgeText}`}
            >
              {state}
            </div>
          ))}
        </div>
        <div className="space-y-3">
          {metricRow(
            "RECEITA TOTAL",
            region.metrics.revenue,
            "currency",
            FONTES_DA_REGIAO.ga4,
          )}
          {metricRow(
            "ROAS",
            region.metrics.roas,
            "number",
            FONTES_DA_REGIAO.ga4ComMidia,
          )}
          {metricRow(
            "TAXA DE CONVERSÃO",
            region.metrics.conversion_rate,
            "percent",
            FONTES_DA_REGIAO.ga4,
          )}
          {metricRow(
            "CPS",
            region.metrics.cps,
            "currency",
            FONTES_DA_REGIAO.ga4ComMidia,
          )}
        </div>
      </div>
    );
  };

  const loadingMessage = !customerUuid
    ? "Selecione uma conta para visualizar os dados."
    : undefined;
  // Sem resumo e sem erro, a busca ainda não voltou. O primeiro desenho acontece antes de o
  // efeito ligar `summaryLoading`, e a tela mostrava "Sem dados disponíveis" por um instante.
  const carregandoResumo = summaryLoading || (summary === null && !erroSummary);

  // ---------- Mapa: destacar regiões ativas no filtro cruzado ----------

  const activeRegionSlugs = useMemo((): Set<string> => {
    if (!drilldown) return new Set();
    const regionFilter = drilldown.filters["region"];
    if (!regionFilter) return new Set();
    // Normaliza: slug é lowercase sem acento
    return new Set(
      regionFilter.values.map((v) =>
        v
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/\s+/g, "-"),
      ),
    );
  }, [drilldown]);

  const hasActiveRegionFilter = activeRegionSlugs.size > 0;

  return (
    <div className="p-6 space-y-6 page-background">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Análise por Região
        </h1>
        <p className="text-muted-foreground">
          Análise detalhada por região das métricas de conversão e engajamento
          do seu e-commerce
        </p>
      </div>

      <GlobalFilter
        rightSlot={
          <CampaignFiltersControl
            customerUuid={campaignCustomerUuid}
            accountId={accountId}
            onSaved={handleCampaignFiltersSaved}
          />
        }
      />

      {/* Filtros Ativos de Cross-Filtering */}
      <ActiveFiltersDisplay />

      {loadingMessage ? (
        <Card className="w-full">
          <CardContent>
            <p className="py-6 text-center text-muted-foreground">
              {loadingMessage}
            </p>
          </CardContent>
        </Card>
      ) : carregandoResumo && summaryRegions.length === 0 ? (
        <Card className="w-full">
          <CardContent>
            <div className="flex justify-center py-6">
              <CarregandoValor
                className="text-2xl text-primary"
                rotulo="Carregando regiões"
              />
            </div>
          </CardContent>
        </Card>
      ) : erroSummary ? (
        <Card className="w-full">
          <CardContent>
            <p className="py-6 text-center text-muted-foreground">
              Não conseguimos carregar os dados de regiões. Tente atualizar a
              página em alguns instantes.
            </p>
          </CardContent>
        </Card>
      ) : summaryRegions.length === 0 ? (
        <Card className="w-full">
          <CardContent>
            <p className="py-6 text-center text-muted-foreground">
              Sem dados disponíveis para o período selecionado.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="w-full">
          <CardHeader>
            <CardTitle>Regiões</CardTitle>
          </CardHeader>
          <CardContent className="relative">
            <CarregandoSobreposto visivel={summaryLoading} />
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-3">
                <div className="grid grid-cols-12 gap-4">
                  {regionGroups.left.map((region) => (
                    <React.Fragment key={region.slug}>
                      {renderRegionCard(region)}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              <div
                className="col-span-6 relative overflow-hidden rounded-lg"
                style={{ minHeight: "760px" }}
              >
                {/* Mapa do Brasil escalado para ocupar todo o espaço */}
                <img
                  src="/mapaBrasil.png"
                  alt="Mapa do Brasil por Regiões"
                  className={`absolute transition-opacity duration-300 ${hasActiveRegionFilter ? "opacity-60" : ""}`}
                  style={{
                    width: "400%",
                    maxWidth: "none",
                    height: "auto",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -52%)",
                  }}
                />
                {/* Overlay indicando filtro ativo */}
                {hasActiveRegionFilter && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                    <div className="bg-background/80 backdrop-blur-sm rounded-lg px-4 py-2 shadow-md border border-border">
                      <p className="text-sm font-medium text-foreground">
                        Filtro ativo:{" "}
                        {Array.from(activeRegionSlugs)
                          .map(
                            (s) =>
                              s.charAt(0).toUpperCase() +
                              s.slice(1).replace(/-/g, " "),
                          )
                          .join(", ")}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <div className="col-span-3">
                <div className="grid grid-cols-12 gap-4">
                  {regionGroups.right.map((region) => (
                    <React.Fragment key={region.slug}>
                      {renderRegionCard(region)}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <StandardizedTable
        title="DADOS GERAIS POR REGIÃO"
        columns={statesColumns}
        data={statesData?.items || []}
        loading={statesLoading}
        pagination={{
          current: statesData?.pagination?.page || statesPage,
          pageSize: STATES_PAGE_SIZE,
          total: statesData?.pagination?.total || 0,
          onPageChange: (pageNumber) => setStatesPage(pageNumber),
        }}
        queryMode="server"
        onQueryChange={(q) => {
          setStatesQuery(q);
          setStatesPage(1);
        }}
        onRowClick={drilldown ? handleStatesRowClick : undefined}
        selectedRowIndex={drilldown ? isStatesRowSelected : undefined}
        clickableRows={!!drilldown}
        className="mt-8"
      />

      {/* Matriz Região x Categoria */}
      {categoryMatrixTable && (
        <StandardizedTable
          title="Categoria do Item / Receita do Item"
          description="Matriz Região × Categoria (receita do item)"
          columns={categoryMatrixTable.columns}
          data={categoryMatrixTable.data}
          loading={categoryMatrixLoading}
          pagination={{
            current: categoryMatrixData?.pagination?.page || categoryMatrixPage,
            pageSize: CATEGORY_MATRIX_PAGE_SIZE,
            total: categoryMatrixData?.pagination?.total || 0,
            onPageChange: (p) => setCategoryMatrixPage(p),
          }}
          queryMode="server"
          onQueryChange={(q) => {
            setCategoryMatrixQuery(q);
            setCategoryMatrixPage(1);
          }}
          onRowClick={drilldown ? handleCategoryMatrixRowClick : undefined}
          selectedRowIndex={drilldown ? isCategoryMatrixRowSelected : undefined}
          clickableRows={!!drilldown}
          className="mt-8"
        />
      )}

      {/* Sem matriz ainda não há colunas para montar a tabela: o aviso ocupa o lugar dela. */}
      {!categoryMatrixTable && categoryMatrixLoading && (
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Categoria do Item / Receita do Item</CardTitle>
          </CardHeader>
          <CardContent>
            <CarregandoConteudo />
          </CardContent>
        </Card>
      )}

      {/* Tabela de Cidades */}
      <StandardizedTable
        title="CIDADES"
        columns={citiesColumns}
        data={citiesData?.items || []}
        loading={citiesLoading}
        pagination={{
          current: citiesData?.pagination?.page || citiesPage,
          pageSize: CITIES_PAGE_SIZE,
          total: citiesData?.pagination?.total || 0,
          onPageChange: (pageNumber) => setCitiesPage(pageNumber),
        }}
        queryMode="server"
        onQueryChange={(q) => {
          setCitiesQuery(q);
          setCitiesPage(1);
        }}
        onRowClick={drilldown ? handleCityRowClick : undefined}
        selectedRowIndex={drilldown ? isCityRowSelected : undefined}
        clickableRows={!!drilldown}
        className="mt-8"
      />

      <StandardizedTable
        title="NOME DO ITEM"
        columns={itemsColumns}
        data={itemsData?.items || []}
        loading={itemsLoading}
        pagination={{
          current: itemsData?.pagination?.page || itemsPage,
          pageSize: ITEMS_PAGE_SIZE,
          total: itemsData?.pagination?.total || 0,
          onPageChange: (pageNumber) => setItemsPage(pageNumber),
        }}
        queryMode="server"
        onQueryChange={(q) => {
          setItemsQuery(q);
          setItemsPage(1);
        }}
        className="mt-8"
      />
    </div>
  );
};

export default RegiaoPage;
